/**
 * DataLayer Proxy
 *
 * Wraps window.dataLayer.push() to:
 * 1. Inject consent state (consentSettings) into every tracking event
 * 2. Buffer tracking events when CMP hasn't determined consent yet
 *
 * Consent injection: Every dataLayer push with an `event` property gets
 * consentSettings injected so GTM can evaluate consent at the event level
 * (replaces Trigger Group dependency).
 *
 * Event buffering: When a CMP (Cookiebot) is present and the user hasn't
 * granted consent, tracking events are cloned into a buffer AND passed
 * through to the dataLayer. Events always reach GTM so Google Consent Mode
 * can send consent pings for behavioral modeling. When consent becomes granted:
 * - Replay buffered events with _queue suffix (for non-Google tags)
 * - Buffer is never discarded on deny — kept until consent is granted or page unloads
 *
 * Google tags fire on the original events via GCM (consent pings when denied,
 * full hits when granted). Non-Google tags fire on the _queue replay events.
 *
 * Layering: GTM, gtag.js and other scripts replace dataLayer.push with their
 * own wrapper, each capturing the push that was active when they loaded.
 * Every install() therefore wraps the push that is active *at that moment*
 * in a new layer with its own captured `next`. The chain stays acyclic and
 * every wrapper below us (GTM container, gtag runtime) still receives each
 * push. install() is a no-op while our own layer is still the outermost
 * push — wrapping ourselves would cut GTM out of the chain.
 */

import type { ConsentManager } from '../consent/consent-manager';

/**
 * Events that are never buffered: GTM's own `gtm.*` events (gtm.js, gtm.dom,
 * gtm.load, history/scroll/click events) are infrastructure, and a replayed
 * `consentUpdate` would carry a stale state — a fresh one is pushed when
 * consent changes.
 */
function isPassthroughEvent(eventName: string): boolean {
  return eventName.startsWith('gtm.') || eventName === 'consentUpdate';
}

/** Marks a push function installed by a DataLayerProxy (value: the owning proxy). */
const PROXY_OWNER = Symbol.for('analyzify.dataLayerProxy');

/** Native Array.prototype.push — used when the dataLayer has no push function yet */
const nativePush = Array.prototype.push;

type PushFn = (...items: any[]) => number;

export class DataLayerProxy {
  private buffer: Record<string, unknown>[] = [];
  private buffering: boolean;
  private consentManager: ConsentManager;
  private dataLayerRef!: any[];
  /**
   * Items this proxy has already processed. When the proxy is installed more
   * than once (layers below and above GTM), the same item travels through
   * several layers — consent is injected and the item is buffered only once.
   */
  private processed = new WeakSet<object>();

  constructor(consentManager: ConsentManager, shouldBuffer: boolean) {
    this.consentManager = consentManager;
    this.buffering = shouldBuffer;
  }

  /**
   * Install the proxy on an existing dataLayer array.
   * Can be called multiple times (e.g., after GTM replaces push).
   * The buffer is preserved across reinstalls.
   */
  install(dataLayer: any[]): void {
    this.dataLayerRef = dataLayer;
    const current = dataLayer.push as PushFn & { [PROXY_OWNER]?: DataLayerProxy };

    // Our layer is still the outermost push — nothing replaced it since the
    // last install. Wrapping again would make our layer call itself and every
    // push would stop at the array, never reaching GTM.
    if (typeof current === 'function' && current[PROXY_OWNER] === this) {
      return;
    }

    const next: PushFn =
      typeof current === 'function' ? current.bind(dataLayer) : nativePush.bind(dataLayer);
    const self = this;
    const proxy = function (...args: any[]): number {
      return self.handlePush(args, next);
    };
    Object.defineProperty(proxy, PROXY_OWNER, { value: this });
    dataLayer.push = proxy;
  }

  /**
   * Intercept dataLayer.push() calls.
   * Injects consent and buffers tracking events, then hands every item to the
   * push this layer wrapped (GTM / gtag / native).
   */
  private handlePush(args: any[], next: PushFn): number {
    let result = this.dataLayerRef.length;

    for (const item of args) {
      if (item && typeof item === 'object' && !this.processed.has(item)) {
        this.processed.add(item);

        // Arguments objects from gtag() calls (e.g., gtag('consent','default',...))
        // have numeric keys but no `event` property — pass through untouched
        const eventName = item.event;
        if (typeof eventName === 'string') {
          // 1. Inject consent state into events that have an `event` property
          //    Skip if the event already has consentSettings (e.g., consentUpdate)
          if (!item.consentSettings) {
            item.consentSettings = this.buildConsentSettings();
          }

          // 2. Always pass through to dataLayer (Google Consent Mode needs to see
          //    all events for consent pings / behavioral modeling).
          //    Additionally clone into buffer while consent is still undetermined.
          //    Checked live: once the visitor grants consent, the CDN's queued
          //    actions push the page's events before flush() runs — those are
          //    post-consent events, not buffer material.
          if (this.buffering && !this.consentManager.canTrack() && !isPassthroughEvent(eventName)) {
            this.buffer.push({ ...item });
          }
        }
      }

      result = next(item);
    }

    return result;
  }

  /**
   * Build the consentSettings object from current ConsentManager state.
   */
  private buildConsentSettings(): Record<string, string> {
    const gcm = this.consentManager.getGCMState();
    return {
      ad_storage: gcm.ad_storage || 'denied',
      analytics_storage: gcm.analytics_storage || 'denied',
      ad_user_data: gcm.ad_user_data || 'denied',
      ad_personalization: gcm.ad_personalization || 'denied',
      personalization_storage: gcm.personalization_storage || 'denied',
      functionality_storage: gcm.functionality_storage,
      security_storage: gcm.security_storage,
      analyzify_consent: (
        gcm.ad_storage === 'granted' && gcm.analytics_storage === 'granted'
      ) ? 'granted' : 'denied',
    };
  }

  /**
   * Flush the buffer: replay all buffered events with _queue suffix.
   * Re-injects current consent state (which is now determined).
   * Non-Google tags in GTM should be configured to fire on both
   * `ee_view_item` and `ee_view_item_queue`.
   */
  flush(): void {
    this.buffering = false;

    for (const item of this.buffer) {
      // Add _queue suffix so Google tags ignore replayed events
      // (Google Consent Mode already handled them)
      if (typeof item.event === 'string') {
        item.event = item.event + '_queue';
      }
      // Re-inject current consent state (consent is now determined/granted)
      item.consentSettings = this.buildConsentSettings();

      // GTM merges ecommerce objects across pushes; clear it before every
      // replayed ecommerce event (the original { ecommerce: null } resets have
      // no `event` key and are never buffered).
      if ('ecommerce' in item) {
        this.dataLayerRef.push({ ecommerce: null });
      }

      // Push through the public dataLayer.push so GTM Preview/Debug sees
      // these events. Since buffering is already false, our proxy won't
      // re-buffer them. consentSettings is already set so won't be re-injected.
      this.dataLayerRef.push(item);
    }

    this.buffer = [];
  }

  /**
   * Discard the buffer: clear all buffered events without replaying.
   * Called when the user denies consent.
   */
  discard(): void {
    this.buffering = false;
    this.buffer = [];
  }

  get isBuffering(): boolean {
    return this.buffering;
  }
}
